export default function SentimentAnalysis({ data }) {
  if (!data) return null;

  // Handle both old format (flat) and new format (by source)
  const google = data.google || {};
  const opentable = data.opentable || {};
  const resy = data.resy || {};
  const social = data.social || {};
  const combined = data.combined || {
    positiveThemes: data.positiveThemes || [],
    negativeThemes: data.negativeThemes || [],
    overallSentiment: '',
  };

  const sources = [
    {
      key: 'google',
      label: 'Google Reviews',
      icon: '⭐',
      color: '#fbbf24',
      rating: google.overallRating,
      totalReviews: google.totalReviews,
      positive: google.positiveThemes || [],
      negative: google.negativeThemes || [],
      emotional: google.emotionalWords || data.emotionalWords || [],
      transactional: google.transactionalWords || data.transactionalWords || [],
    },
    {
      key: 'opentable',
      label: 'OpenTable',
      icon: '🍽️',
      color: '#da3743',
      rating: opentable.rating,
      positive: opentable.positiveThemes || [],
      negative: opentable.negativeThemes || [],
      notes: opentable.notes,
    },
    {
      key: 'resy',
      label: 'Resy',
      icon: '📱',
      color: '#c62828',
      notes: resy.notes,
    },
    {
      key: 'social',
      label: 'Social Media',
      icon: '📸',
      color: '#e040fb',
      positive: social.positiveThemes || [],
      negative: social.negativeThemes || [],
      notes: social.notes,
    },
  ];

  // Filter to sources that have data
  const activeSources = sources.filter(s =>
    s.rating || s.notes || (s.positive?.length > 0 && s.positive[0]) || (s.negative?.length > 0 && s.negative[0])
  );

  return (
    <div className="card p-6">
      <h3 className="font-display text-lg text-white mb-1">Guest Sentiment Analysis</h3>
      <p className="text-xs text-neutral-500 mb-5">Sentiment broken out by source — based on real data</p>

      {/* Combined overview */}
      {combined.overallSentiment && (
        <div className="bg-neutral-800/40 rounded-lg p-4 mb-5">
          <p className="text-sm text-neutral-300">{combined.overallSentiment}</p>
        </div>
      )}

      {/* Combined themes */}
      {(combined.positiveThemes?.filter(Boolean).length > 0 || combined.negativeThemes?.filter(Boolean).length > 0) && (
        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          {combined.positiveThemes?.filter(Boolean).length > 0 && (
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
              <p className="text-xs text-emerald-400 font-semibold mb-2 uppercase tracking-wider">Overall Positive</p>
              {combined.positiveThemes.filter(Boolean).map((t, i) => (
                <p key={i} className="text-xs text-neutral-400 mb-1">+ {t}</p>
              ))}
            </div>
          )}
          {combined.negativeThemes?.filter(Boolean).length > 0 && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4">
              <p className="text-xs text-red-400 font-semibold mb-2 uppercase tracking-wider">Overall Concerns</p>
              {combined.negativeThemes.filter(Boolean).map((t, i) => (
                <p key={i} className="text-xs text-neutral-400 mb-1">- {t}</p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* By source */}
      {activeSources.length > 0 && (
        <div className="space-y-4">
          {activeSources.map(source => (
            <div key={source.key} className="border border-neutral-800 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">{source.icon}</span>
                <h4 className="text-sm font-semibold text-white">{source.label}</h4>
                {source.rating && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: source.color + '20', color: source.color }}>
                    {source.rating}{source.key === 'google' ? '/5' : ''} {source.totalReviews ? `(${source.totalReviews} reviews)` : ''}
                  </span>
                )}
              </div>

              {source.notes && !source.positive?.filter(Boolean).length && (
                <p className="text-xs text-neutral-400">{source.notes}</p>
              )}

              {(source.positive?.filter(Boolean).length > 0 || source.negative?.filter(Boolean).length > 0) && (
                <div className="grid grid-cols-2 gap-3">
                  {source.positive?.filter(Boolean).length > 0 && (
                    <div>
                      <p className="text-[10px] text-emerald-400 font-semibold mb-1">Positive</p>
                      {source.positive.filter(Boolean).map((t, i) => (
                        <p key={i} className="text-xs text-neutral-400 mb-0.5">+ {t}</p>
                      ))}
                    </div>
                  )}
                  {source.negative?.filter(Boolean).length > 0 && (
                    <div>
                      <p className="text-[10px] text-red-400 font-semibold mb-1">Negative</p>
                      {source.negative.filter(Boolean).map((t, i) => (
                        <p key={i} className="text-xs text-neutral-400 mb-0.5">- {t}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Emotional vs transactional word analysis (Google) */}
              {source.emotional?.filter(Boolean).length > 0 && (
                <div className="mt-3 pt-3 border-t border-neutral-800">
                  <p className="text-[10px] text-neutral-500 mb-1">Language Analysis</p>
                  <div className="flex flex-wrap gap-1.5">
                    {source.emotional.filter(Boolean).map((w, i) => (
                      <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-brand-gold/10 text-brand-gold">{w}</span>
                    ))}
                    {source.transactional?.filter(Boolean).map((w, i) => (
                      <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-800 text-neutral-400">{w}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Word Cloud — single words/short phrases sized by frequency */}
      {(() => {
        // Extract individual words from all sources
        const stopWords = new Set(['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'is', 'was', 'are', 'were', 'been', 'be', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'can', 'this', 'that', 'these', 'those', 'it', 'its', 'they', 'them', 'their', 'we', 'our', 'you', 'your', 'i', 'my', 'me', 'not', 'no', 'very', 'really', 'just', 'also', 'so', 'than', 'then', 'here', 'there', 'when', 'where', 'what', 'which', 'who', 'how', 'all', 'each', 'every', 'both', 'few', 'more', 'most', 'other', 'some', 'such', 'only', 'own', 'same', 'too', 'as', 'if', 'from', 'about', 'up', 'out', 'off', 'over', 'into', 'get', 'got']);

        const wordCounts = {};

        // Process emotional words
        (google.emotionalWords || data.emotionalWords || []).filter(Boolean).forEach(phrase => {
          phrase.split(/\s+/).forEach(w => {
            const clean = w.toLowerCase().replace(/[^a-z']/g, '');
            if (clean.length > 2 && !stopWords.has(clean)) {
              if (!wordCounts[clean]) wordCounts[clean] = { count: 0, type: 'emotional' };
              wordCounts[clean].count++;
            }
          });
        });

        // Process transactional words
        (google.transactionalWords || data.transactionalWords || []).filter(Boolean).forEach(phrase => {
          phrase.split(/\s+/).forEach(w => {
            const clean = w.toLowerCase().replace(/[^a-z']/g, '');
            if (clean.length > 2 && !stopWords.has(clean)) {
              if (!wordCounts[clean]) wordCounts[clean] = { count: 0, type: 'transactional' };
              wordCounts[clean].count++;
            }
          });
        });

        // Process negative themes — extract key words
        [...(google.negativeThemes || []), ...(combined.negativeThemes || [])].filter(Boolean).forEach(phrase => {
          phrase.split(/\s+/).forEach(w => {
            const clean = w.toLowerCase().replace(/[^a-z']/g, '');
            if (clean.length > 2 && !stopWords.has(clean)) {
              if (!wordCounts[clean]) wordCounts[clean] = { count: 0, type: 'negative' };
              wordCounts[clean].count++;
            }
          });
        });

        // Process positive themes too
        [...(google.positiveThemes || []), ...(combined.positiveThemes || [])].filter(Boolean).forEach(phrase => {
          phrase.split(/\s+/).forEach(w => {
            const clean = w.toLowerCase().replace(/[^a-z']/g, '');
            if (clean.length > 2 && !stopWords.has(clean)) {
              if (!wordCounts[clean]) wordCounts[clean] = { count: 0, type: 'emotional' };
              wordCounts[clean].count++;
            }
          });
        });

        const words = Object.entries(wordCounts)
          .map(([word, { count, type }]) => ({ word, count, type }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 40); // Top 40 words

        if (words.length === 0) return null;

        const maxCount = words[0].count;
        const minSize = 0.7;
        const maxSize = 2.0;

        const colorMap = {
          emotional: { text: '#C9A668', glow: 'rgba(201, 166, 104, 0.15)' },
          transactional: { text: '#a3a3a3', glow: 'rgba(163, 163, 163, 0.1)' },
          negative: { text: '#f87171', glow: 'rgba(248, 113, 113, 0.12)' },
        };

        // Shuffle for visual variety
        const shuffled = [...words].sort((a, b) => {
          const hash = (s) => s.word.split('').reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0), 0);
          return hash(a) - hash(b);
        });

        return (
          <div className="mt-5 pt-5 border-t border-neutral-800">
            <p className="text-xs text-neutral-500 font-semibold mb-3 uppercase tracking-wider">Guest Language Cloud</p>
            <div className="relative flex flex-wrap items-center justify-center gap-x-4 gap-y-2 p-6 bg-neutral-900/50 rounded-xl border border-neutral-800/50 min-h-[120px]">
              {shuffled.map((w, i) => {
                const sizePct = w.count / maxCount;
                const fontSize = minSize + sizePct * (maxSize - minSize);
                const opacity = 0.5 + sizePct * 0.5;
                const colors = colorMap[w.type];
                return (
                  <span
                    key={i}
                    className="font-semibold transition-all duration-300 hover:scale-110 cursor-default"
                    style={{
                      fontSize: `${fontSize}rem`,
                      lineHeight: 1.3,
                      color: colors.text,
                      opacity,
                      textShadow: `0 0 ${sizePct * 20}px ${colors.glow}`,
                    }}
                    title={`"${w.word}" — ${w.type} (${w.count}x)`}
                  >
                    {w.word}
                  </span>
                );
              })}
            </div>
            <div className="flex justify-center gap-6 mt-3">
              <span className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#C9A668' }} />
                <span className="text-neutral-500">Emotional</span>
              </span>
              <span className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full bg-neutral-500" />
                <span className="text-neutral-500">Transactional</span>
              </span>
              <span className="flex items-center gap-1.5 text-[11px]"><span className="w-2 h-2 rounded-full bg-red-400" />
                <span className="text-neutral-500">Negative</span>
              </span>
            </div>
          </div>
        );
      })()}

      {activeSources.length === 0 && combined.positiveThemes?.filter(Boolean).length === 0 && (
        <p className="text-neutral-600 text-sm text-center py-4">No sentiment data available. Run AI Audit with Google Reviews enabled.</p>
      )}
    </div>
  );
}
